export const TITLE_BLOCK_SEPARATOR = ' · ';

export const TITLE_BLOCK_KEYS = [
  'deadline_title',
  'matter_name',
  'matter_number',
  'short_description',
] as const;

export type TitleBlockKey = (typeof TITLE_BLOCK_KEYS)[number];

export type ShowAs = 'busy' | 'free';
export type ReminderOption = 'none' | '1_day' | '1_week' | '2_weeks';
export type InviteMembers = 'off' | 'assignee' | 'all';

export interface TitleBlock {
  key: TitleBlockKey;
  enabled: boolean;
}

export interface CalendarSyncPreferences {
  show_as: ShowAs;
  all_day: boolean;
  start_time: string;
  reminder: ReminderOption;
  invite_members: InviteMembers;
  title_blocks: TitleBlock[];
  timezone: string;
}

export const TITLE_BLOCK_LABELS: Record<TitleBlockKey, string> = {
  deadline_title: 'Full deadline description',
  matter_name: 'Matter name',
  matter_number: 'Matter number',
  short_description: 'Short description',
};

export const SAMPLE_TITLE_VALUES: Record<TitleBlockKey, string> = {
  deadline_title: 'File defence to the statement of claim',
  matter_name: 'Acme Ltd v Beta Corp',
  matter_number: '2024-001',
  short_description: 'File defence',
};

export const DEFAULT_CALENDAR_SYNC_PREFERENCES: CalendarSyncPreferences = {
  show_as: 'free',
  all_day: true,
  start_time: '09:00',
  reminder: 'none',
  invite_members: 'off',
  title_blocks: [
    { key: 'deadline_title', enabled: true },
    { key: 'matter_name', enabled: false },
    { key: 'matter_number', enabled: false },
    { key: 'short_description', enabled: false },
  ],
  timezone: 'UTC',
};

export function composeOutlookSubject(
  blocks: TitleBlock[],
  values: Partial<Record<TitleBlockKey, string | null | undefined>> = SAMPLE_TITLE_VALUES,
): string {
  const fullTitle = (values.deadline_title ?? '').trim();
  const titleOn = blocks.some((block) => block.enabled && block.key === 'deadline_title');
  const parts = blocks
    .filter((block) => block.enabled)
    .map((block) => {
      const part = (values[block.key] ?? '').trim();
      if (block.key === 'short_description' && titleOn && part === fullTitle) return '';
      return part;
    })
    .filter((part) => part.length > 0);
  if (parts.length > 0) return parts.join(TITLE_BLOCK_SEPARATOR);
  return (values.deadline_title ?? '').trim() || 'Deadline';
}

export function moveTitleBlock(blocks: TitleBlock[], index: number, delta: number): TitleBlock[] {
  const nextIndex = index + delta;
  if (index < 0 || nextIndex < 0 || index >= blocks.length || nextIndex >= blocks.length) {
    return blocks;
  }
  const next = [...blocks];
  const [moved] = next.splice(index, 1);
  next.splice(nextIndex, 0, moved);
  return next;
}

export function toggleTitleBlock(blocks: TitleBlock[], key: TitleBlockKey, enabled: boolean): TitleBlock[] {
  const next = blocks.map((block) => (block.key === key ? { ...block, enabled } : block));
  if (!next.some((block) => block.enabled)) {
    return next.map((block) =>
      block.key === 'deadline_title' ? { ...block, enabled: true } : block,
    );
  }
  return next;
}
